import { useRef, useState, useEffect } from 'react';
import {
  Bars3Icon,
  TrashIcon,
  XMarkIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { TextField, Textarea } from '@/shared/components/TextField';
import { Button } from '@/shared/components/Buttons';
import { Tooltip } from '@/shared/components/Tooltip';
import type { FormSection, FormField } from '../../types';
import { TASK_FORM_LABELS } from '../../constants/labels';
import FieldEditor from './FieldEditor';

let activeDragSectionId: string | null = null;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SectionErrors {
  name?: string;
  description?: string;
  minFields?: string;
  fields?: Record<
    string,
    {
      label?: string;
      type?: string;
      minOptions?: string;
      options?: Record<string, { label?: string }>;
      rangeMin?: string;
      rangeMax?: string;
      rangeUnit?: string;
      attachmentFileTypes?: string;
      attachmentMaxSize?: string;
      attachmentMaxFiles?: string;
    }
  >;
}

export interface SectionEditorProps {
  section: FormSection;
  sectionIndex: number;
  onChange: (updated: FormSection) => void;
  onRemove: () => void;
  isOnly?: boolean;
  isFirst?: boolean;
  errors?: SectionErrors | undefined;
  onDragStart?: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: () => void;
}

// ─── SectionEditor ────────────────────────────────────────────────────────────

const SectionEditor: React.FC<SectionEditorProps> = ({
  section,
  sectionIndex,
  onChange,
  onRemove,
  isOnly = false,
  isFirst = false,
  errors = {},
  onDragStart,
  onDragOver,
  onDrop,
}) => {
  const fieldDragIndexRef = useRef<number | null>(null);
  const [isSectionDragOver, setIsSectionDragOver] = useState(false);
  const [hoveredFieldId, setHoveredFieldId] = useState<string | null>(null);
  const [fieldsNoteDismissed, setFieldsNoteDismissed] = useState(false);

  useEffect(() => {
    const clear = () => {
      setIsSectionDragOver(false);
      setHoveredFieldId(null);
    };
    document.addEventListener('dragend', clear);
    return () => document.removeEventListener('dragend', clear);
  }, []);

  const addField = () => {
    const newField: FormField = {
      id: crypto.randomUUID(),
      type: '',
      label: '',
      required: true,
    };
    onChange({ ...section, fields: [...section.fields, newField] });
  };

  const updateField = (idx: number, updated: FormField) => {
    const fields = section.fields.map((f, i) => (i === idx ? updated : f));
    onChange({ ...section, fields });
  };

  const removeField = (idx: number) => {
    onChange({
      ...section,
      fields: section.fields.filter((_, i) => i !== idx),
    });
  };

  const onFieldDrop = (toIdx: number) => {
    const from = fieldDragIndexRef.current;
    if (from === null || from === toIdx) {
      return;
    }
    const fields = [...section.fields];
    const a = fields[from];
    const b = fields[toIdx];
    if (!a || !b) {
      return;
    }
    fields[from] = b;
    fields[toIdx] = a;
    onChange({ ...section, fields });
    fieldDragIndexRef.current = null;
  };

  return (
    <div
      className="flex items-start gap-2"
      draggable={!isOnly}
      onDragStart={(e) => {
        const ghost = document.createElement('div');
        ghost.style.cssText =
          'position:fixed;top:-9999px;left:0;width:360px;background:white;border:1px solid #DCDDE0;border-radius:12px;overflow:hidden;box-shadow:0 4px 12px rgba(0,0,0,0.12);pointer-events:none;';

        const nameWrap = document.createElement('div');
        nameWrap.style.cssText =
          'padding:10px 16px;border-bottom:1px solid #DCDDE0;';
        const nameLabel = document.createElement('p');
        nameLabel.style.cssText =
          'font-size:11px;color:#878B8E;margin:0 0 3px;';
        nameLabel.textContent = TASK_FORM_LABELS.SECTION_NAME_PLACEHOLDER;
        const nameValue = document.createElement('p');
        nameValue.style.cssText =
          'font-size:14px;font-weight:500;color:#474A4C;margin:0;';
        nameValue.textContent =
          section.name.trim() || TASK_FORM_LABELS.SECTION_NAME_PLACEHOLDER;
        nameWrap.appendChild(nameLabel);
        nameWrap.appendChild(nameValue);
        ghost.appendChild(nameWrap);

        if (section.description.trim()) {
          const descWrap = document.createElement('div');
          descWrap.style.cssText = 'padding:10px 16px;';
          const descLabel = document.createElement('p');
          descLabel.style.cssText =
            'font-size:11px;color:#878B8E;margin:0 0 3px;';
          descLabel.textContent = TASK_FORM_LABELS.SECTION_DESC_PLACEHOLDER;
          const descValue = document.createElement('p');
          descValue.style.cssText =
            'font-size:13px;color:#474A4C;margin:0;white-space:pre-wrap;word-break:break-word;';
          descValue.textContent = section.description;
          descWrap.appendChild(descLabel);
          descWrap.appendChild(descValue);
          ghost.appendChild(descWrap);
        }

        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, 180, 20);
        requestAnimationFrame(() => document.body.removeChild(ghost));
        onDragStart?.();
      }}
      onDragEnter={() => {
        if (!activeDragSectionId) {
          setIsSectionDragOver(true);
        }
      }}
      onDragOver={(e) => {
        if (!activeDragSectionId) {
          onDragOver?.(e);
        }
      }}
      onDragLeave={(e) => {
        if (
          !activeDragSectionId &&
          !e.currentTarget.contains(e.relatedTarget as Node)
        ) {
          setIsSectionDragOver(false);
        }
      }}
      onDrop={() => {
        setIsSectionDragOver(false);
        if (!activeDragSectionId) {
          onDrop?.();
        }
      }}
    >
      {/* Drag handle — outside the card */}
      <div className="shrink-0 mt-4">
        <Tooltip
          content={TASK_FORM_LABELS.TOOLTIP_REORDER_SECTION}
          forceShow
          side="top"
          align="center"
          triggerWrapContent
          contentMinWidth={160}
        >
          <Bars3Icon
            className={`w-6 h-6 ${isOnly ? 'text-[#DCDDE0] cursor-not-allowed' : 'text-[#474A4C] cursor-grab'}`}
            aria-label="Przeciągnij sekcję"
          />
        </Tooltip>
      </div>

      {/* Card */}
      <div
        className={`flex-1 border rounded-xl transition-colors ${isSectionDragOver ? 'border-[#2A7ABA] bg-[#EAF3FB]' : 'border-[#DCDDE0] bg-white'}`}
        data-error-field={
          errors.name ||
          errors.description ||
          errors.minFields ||
          (errors.fields && Object.keys(errors.fields).length > 0)
            ? 'true'
            : undefined
        }
      >
        {/* Section name */}
        <div className="px-4 py-3">
          <TextField
            label={TASK_FORM_LABELS.SECTION_NAME_PLACEHOLDER}
            value={section.name}
            onChange={(e) =>
              onChange({ ...section, name: e.target.value.slice(0, 200) })
            }
            maxLength={200}
            width={'60%'}
            error={Boolean(errors.name)}
            errorMessage={errors.name ?? ''}
            dataTestId={`section-name-input-${sectionIndex}`}
          />
        </div>

        {/* Section body */}
        <div className="px-4 py-4 flex flex-col gap-5">
          <Textarea
            label={
              <>
                Opis{' '}
                <span
                  style={{
                    fontSize: 12,
                    lineHeight: '16px',
                    letterSpacing: '0.4px',
                    color: '#474A4C',
                  }}
                >
                  (Opcjonalne)
                </span>
              </>
            }
            placeholder={TASK_FORM_LABELS.SECTION_DESC_PLACEHOLDER}
            value={section.description}
            onChange={(e) =>
              onChange({
                ...section,
                description: e.target.value.slice(0, 500),
              })
            }
            maxLength={500}
            fullWidth
            rows={3}
            error={Boolean(errors.description)}
            errorMessage={errors.description ?? ''}
            dataTestId={`section-desc-input-${sectionIndex}`}
          />

          {/* Fields */}
          <div className="flex flex-col gap-1">
            <p className="text-base font-normal text-[#474A4C]">
              {TASK_FORM_LABELS.FIELDS_SECTION_TITLE} ({section.fields.length})
            </p>
            <p className="text-sm text-[#474A4C] mb-3">
              {TASK_FORM_LABELS.FIELDS_SECTION_DESC}
            </p>

            {isFirst && !fieldsNoteDismissed && (
              <div className="flex items-center gap-3 bg-[#EAF3FF] rounded-lg px-4 py-3 mb-2">
                <InformationCircleIcon className="w-5 h-5 text-[#1E7CE8] shrink-0" />
                <p className="text-sm text-[#1E7CE8] flex-1">
                  <strong>Uwaga:</strong>{' '}
                  {TASK_FORM_LABELS.FIELDS_REQUIRED_NOTE}
                </p>
                <button
                  type="button"
                  onClick={() => setFieldsNoteDismissed(true)}
                  className="text-[#1E7CE8] hover:text-[#1565C0] shrink-0 cursor-pointer"
                  aria-label="Zamknij"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            )}

            {errors.minFields && (
              <p className="text-xs text-error-40">{errors.minFields}</p>
            )}

            <div className="flex flex-col gap-8">
              {section.fields.map((field, idx) => {
                const fe = errors.fields?.[field.id];
                return (
                  <FieldEditor
                    key={field.id}
                    field={field}
                    sectionIndex={sectionIndex}
                    fieldIndex={idx}
                    onChange={(updated) => updateField(idx, updated)}
                    onRemove={() => removeField(idx)}
                    isOnly={section.fields.length === 1}
                    {...(fe?.label !== undefined && { labelError: fe.label })}
                    {...(fe?.type !== undefined && { typeError: fe.type })}
                    {...(fe?.minOptions !== undefined && {
                      minOptionsError: fe.minOptions,
                    })}
                    {...(fe?.options !== undefined && {
                      optionErrors: fe.options,
                    })}
                    {...(fe?.rangeMin !== undefined && {
                      rangeMinError: fe.rangeMin,
                    })}
                    {...(fe?.rangeMax !== undefined && {
                      rangeMaxError: fe.rangeMax,
                    })}
                    {...(fe?.rangeUnit !== undefined && {
                      rangeUnitError: fe.rangeUnit,
                    })}
                    {...(fe?.attachmentFileTypes !== undefined && {
                      attachmentFileTypesError: fe.attachmentFileTypes,
                    })}
                    {...(fe?.attachmentMaxSize !== undefined && {
                      attachmentMaxSizeError: fe.attachmentMaxSize,
                    })}
                    {...(fe?.attachmentMaxFiles !== undefined && {
                      attachmentMaxFilesError: fe.attachmentMaxFiles,
                    })}
                    isDragOver={hoveredFieldId === field.id}
                    onDragEnterField={() => {
                      if (activeDragSectionId === section.id) {
                        setHoveredFieldId(field.id);
                      }
                    }}
                    onDragLeaveField={(e) => {
                      if (
                        activeDragSectionId === section.id &&
                        !e.currentTarget.contains(e.relatedTarget as Node)
                      ) {
                        setHoveredFieldId((prev) =>
                          prev === field.id ? null : prev
                        );
                      }
                    }}
                    onDragStart={() => {
                      fieldDragIndexRef.current = idx;
                      activeDragSectionId = section.id;
                    }}
                    onDragOver={(e) => {
                      if (activeDragSectionId === section.id) {
                        e.preventDefault();
                      }
                    }}
                    onDrop={() => {
                      setHoveredFieldId(null);
                      onFieldDrop(idx);
                    }}
                    onDragEnd={() => {
                      activeDragSectionId = null;
                    }}
                  />
                );
              })}
            </div>

            {section.fields.length >= 10 ? (
              <Tooltip
                content={TASK_FORM_LABELS.TOOLTIP_MAX_FIELDS}
                side="top"
                align="center"
                forceShow
                triggerTabIndex={-1}
                cursor="not-allowed"
              >
                <Button
                  type="button"
                  label={TASK_FORM_LABELS.BUTTON_ADD_FIELD}
                  buttonType="Square"
                  size="Small"
                  onClick={addField}
                  disabled
                  customBorderColor="#DCDDE0"
                  customBgColor="transparent"
                  customTextColor="#474A4C"
                  customFontWeight={500}
                  icon={<PlusIcon className="w-5 h-5" />}
                  showIcon
                  className="mt-2 w-full hover:!bg-[#F0F0F0]"
                  data-testid={`add-field-button-${sectionIndex}`}
                />
              </Tooltip>
            ) : (
              <Button
                type="button"
                label={TASK_FORM_LABELS.BUTTON_ADD_FIELD}
                buttonType="Square"
                size="Small"
                onClick={addField}
                customBorderColor="#DCDDE0"
                customBgColor="transparent"
                customTextColor="#474A4C"
                customFontWeight={500}
                icon={<PlusIcon className="w-5 h-5" />}
                showIcon
                className="mt-2 w-full hover:!bg-[#F0F0F0]"
                data-testid={`add-field-button-${sectionIndex}`}
              />
            )}
          </div>
        </div>
      </div>

      {/* Delete button — outside the card */}
      <div className="shrink-0 mt-3">
        <Tooltip
          content={TASK_FORM_LABELS.TOOLTIP_DELETE_SECTION}
          forceShow
          side="top"
          align="center"
          triggerWrapContent
          triggerTabIndex={-1}
          contentMinWidth={100}
        >
          <button
            type="button"
            onClick={isOnly ? undefined : onRemove}
            disabled={isOnly}
            className={
              isOnly
                ? 'text-[#DCDDE0] cursor-not-allowed'
                : 'text-red-500 hover:text-red-700 cursor-pointer'
            }
            aria-label="Usuń sekcję"
            data-testid={`delete-section-button-${sectionIndex}`}
          >
            <TrashIcon className="w-6 h-6" />
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

export default SectionEditor;
